import React, { useEffect, useState } from "react";
import axios from "axios";
import { Box, Button, Dialog, DialogContent, IconButton, Link, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import LinkIcon from "@mui/icons-material/Link";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import PublicationFilters from "./PublicationFilters"
import GroupBDRBiologists from "../FieldTags/GroupBDRBiologists";
import HistoryLogElement from "./HistoryLogElement";
import BASE_URL from "../../config/apiConfig";

const Publications = ({ publications, handleChange, xplans }) => {
    const newPlan = window.location.pathname === "/xplans/new";
    const [filteredPublications, setFilteredPublications] = useState([]);
    const [allPublications, setAllPublications] = useState([]);
    const [selectedPublications, setSelectedPublications] = useState([]);
    const [searchTerms, setSearchTerms] = useState("");
    const [filters, setFilters] = useState({});
    const [openFilterWindow, setOpenFilterWindow] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        axios.get(BASE_URL + "/api/publications", { withCredentials: true }).then((res) => {
            setFilteredPublications(res.data);
            setAllPublications(res.data);
        });
    }, []);

    useEffect(() => {
        if (!publications) {
            setSelectedPublications([]);
            return;
        }

        const ids = publications
            .split(",")
            .map((id) => parseInt(id.trim()))
            .filter((id) => !isNaN(id));

        const selected = allPublications.filter((pub) => ids.includes(pub.id));
        setSelectedPublications(selected);
    }, [publications, allPublications]);

    useEffect(() => {
        const handleClick = () => {
            setOpenFilterWindow(false);
        };

        if (openFilterWindow) {
            document.addEventListener("click", handleClick);
        } else {
            document.removeEventListener("click", handleClick);
        }

        return () => {
            document.removeEventListener("click", handleClick);
        };
    }, [openFilterWindow]);

    useEffect(() => {
        setFilteredPublications(allPublications
            .filter((pub) => containSearchTerms(pub, searchTerms.toLowerCase()))
            .filter((pub) => matchFilters(pub))
        );
    }, [searchTerms, filters]);

    const containSearchTerms = (pub, searchTerms) => {
        return (
            pub.pmid?.toString().toLowerCase().includes(searchTerms) ||
            pub.pmcid?.toString().toLowerCase().includes(searchTerms) ||
            pub.title?.toLowerCase().includes(searchTerms) ||
            pub.authors?.toLowerCase().includes(searchTerms)
        );
    }

    const handleSearch = (e) => {
        setSearchTerms(e.target.value);
    }

    const handleSearchClear = () => {
        setSearchTerms("");
    }

    const matchFilters = (pub) => {
        for (const fieldName in filters) {
            for (const fieldType of filters[fieldName]) {
                if (fieldType === "All") {
                    if (pub[fieldName].toString() !== fieldType.toString()) {
                        return false;
                    }
                } else {
                    if (!pub[fieldName]?.toString().includes(fieldType.toString())) {
                        return false;
                    }
                }
            }
        }
        return true;
    }

    const updatePublications = (ids) => {
        handleChange("publications", ids.join(","));
    };

    const handleRemove = (idToRemove) => {
        const newIds = selectedPublications
            .filter((pub) => pub.id !== idToRemove)
            .map((pub) => pub.id);
        updatePublications(newIds);
    };

    const handleAdd = (pub) => {
        const currentIds = selectedPublications.map((p) => p.id);
        if (!currentIds.includes(pub.id)) {
            const newIds = [...currentIds, pub.id];
            updatePublications(newIds);
        }
        setIsOpen(false);
    };

    const renderLinkCell = (url, text) => (
        <Link href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
            {text}
        </Link>
    );

    return (
        <div id="publications" className="planField">
            <GroupBDRBiologists />
            <Box component={Paper} p={2} my={2} sx={{ boxShadow: "none", border: "1px solid #ccc" }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography
                        sx={{
                            position: "relative",
                            fontSize: "15px",
                            fontWeight: 600,
                            color: "#1f1f1f",
                            backgroundColor: "#fff",
                            top: -33,
                            left: -8,
                            px: 0.5,
                        }}
                    >
                        Publications
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => setIsOpen(true)}
                    >
                        Add Publication
                    </Button>
                </Box>

                {selectedPublications?.length > 0 &&
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: "64px" }}></TableCell>
                                <TableCell sx={{ width: "100px" }}>PMID</TableCell>
                                <TableCell sx={{ width: "128px" }}>PMCID</TableCell>
                                <TableCell>Title</TableCell>
                                <TableCell sx={{ width: "64px" }}>Year</TableCell>
                                <TableCell sx={{ width: "164px" }}>Journal</TableCell>
                                <TableCell sx={{ width: "144px" }}>Category</TableCell>
                                <TableCell sx={{ width: "72px" }} align="center">DOI</TableCell>
                                <TableCell sx={{ width: "72px" }} align="center">PDF</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {selectedPublications.map((pub) => (
                                <TableRow
                                    key={pub.id}
                                    onClick={() => setIsOpen(true)}
                                >
                                    <TableCell>
                                        <IconButton
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemove(pub.id);
                                            }}
                                        >
                                            <CloseIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </TableCell>
                                    <TableCell>
                                        {pub.pmid && renderLinkCell(`https://pubmed.ncbi.nlm.nih.gov/${pub.pmid}/`, pub.pmid)}
                                    </TableCell>
                                    <TableCell>
                                        {pub.pmcid && renderLinkCell(`https://pmc.ncbi.nlm.nih.gov/articles/${pub.pmcid}/`, pub.pmcid)}
                                    </TableCell>
                                    <TableCell>{pub.title}</TableCell>
                                    <TableCell>{pub.year}</TableCell>
                                    <TableCell>{pub.journal}</TableCell>
                                    <TableCell>{pub.category}</TableCell>
                                    <TableCell>
                                        {pub.doi && (
                                            <IconButton
                                                href={pub.doi}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <LinkIcon color="action" />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {pub.pdf && (
                                            <IconButton
                                                href={pub.pdf}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <PictureAsPdfIcon color="action" />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                }

                <Dialog
                    open={isOpen}
                    maxWidth={false}
                    fullWidth
                    onClose={(event, reason) => {
                        if (reason === "backdropClick" || reason === "escapeKeyDown") {
                            setIsOpen(false);
                        }
                    }}
                    PaperProps={{ sx: { height: "95%", maxHeight: "95%" } }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            backgroundColor: "#e0e0e0",
                            padding: "8px 16px",
                        }}
                    >
                        <Typography sx={{ fontWeight: "bold" }}>Select a Publication</Typography>
                        <Typography>Email{" "}<Link href="mailto:dct@immunetolerance.org" underline="hover">dct@immunetolerance.org</Link>{" "}if the publication you are looking for is not on this list.</Typography>
                    </Box>
                    <DialogContent sx={{ padding: 0 }}>
                        <div className="dataActions">
                            <div className="dataSearch">
                                <TextField
                                    className="dataSearchField"
                                    placeholder="Title, Author, PMID, or PMCID"
                                    value={searchTerms}
                                    onChange={handleSearch}
                                    InputProps={{
                                        startAdornment: <SearchIcon className="dataSearchIcon" fontSize="small"/>,
                                        endAdornment: searchTerms.length ? (
                                            <IconButton onClick={() => handleSearchClear()}>
                                                <ClearIcon className="dataClearIcon" fontSize="small"/>
                                            </IconButton>
                                        ) : null,
                                    }}
                                    sx={{ fontSize: "15px", height: "48px", width: "576px", padding: 0, margin: 0 }}
                                />
                                <div className="dataTableResults">
                                    <span>Results: </span>{filteredPublications.length}
                                </div>
                            </div>
                            <div className="dataSortFilterAdd">
                                <a
                                    className="dataFilter"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setOpenFilterWindow((prev) => !prev);
                                    }}
                                    style={{ backgroundColor: Object.values(filters).some(value => !!value && value.length > 0) ? "#cceeff" : "inherit" }}
                                >
                            <span className="dataActionIcon">
                                <FilterListIcon/>
                            </span>
                                    <span className="dataActionName">Filters</span>
                                </a>
                                {openFilterWindow && (
                                    <PublicationFilters
                                        filters={filters}
                                        setFilters={setFilters}
                                        publications={filteredPublications}
                                    />
                                )}
                            </div>
                        </div>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: "100px", backgroundColor: "#f5f7fd" }}>PMID</TableCell>
                                    <TableCell sx={{ width: "128px", backgroundColor: "#f5f7fd" }}>PMCID</TableCell>
                                    <TableCell sx={{ backgroundColor: "#f5f7fd" }}>Title</TableCell>
                                    <TableCell sx={{ width: "64px", backgroundColor: "#f5f7fd" }}>Year</TableCell>
                                    <TableCell sx={{ width: "164px", backgroundColor: "#f5f7fd" }}>Journal</TableCell>
                                    <TableCell sx={{ width: "144px", backgroundColor: "#f5f7fd" }}>Category</TableCell>
                                    <TableCell sx={{ width: "480px", backgroundColor: "#f5f7fd" }}>Authors</TableCell>
                                    <TableCell sx={{ width: "72px", backgroundColor: "#f5f7fd" }} align="center">DOI</TableCell>
                                    <TableCell sx={{ width: "72px", backgroundColor: "#f5f7fd" }} align="center">PDF</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {[...filteredPublications].sort((a, b) => {
                                    const aPub = selectedPublications.some(p => p.id === a.id);
                                    const bPub = selectedPublications.some(p => p.id === b.id);
                                    return bPub - aPub;
                                }).map((pub) => (
                                    <TableRow
                                        key={pub.id}
                                        onClick={() => handleAdd(pub)}
                                        sx={{
                                            cursor: "pointer",
                                            backgroundColor: selectedPublications.some(p => p.id === pub.id) ? "#e0e0e0" : "inherit",
                                            '&:hover': { backgroundColor: selectedPublications.some(p => p.id === pub.id) ? "#e0e0e0" : "#f5f5f5" }
                                        }}
                                    >
                                        <TableCell>
                                            {pub.pmid && renderLinkCell(`https://pubmed.ncbi.nlm.nih.gov/${pub.pmid}/`, pub.pmid)}
                                        </TableCell>
                                        <TableCell>
                                            {pub.pmcid && renderLinkCell(`https://pmc.ncbi.nlm.nih.gov/articles/${pub.pmcid}/`, pub.pmcid)}
                                        </TableCell>
                                        <TableCell>{pub.title}</TableCell>
                                        <TableCell>{pub.year}</TableCell>
                                        <TableCell>{pub.journal}</TableCell>
                                        <TableCell>{pub.category}</TableCell>
                                        <TableCell>{pub.authors}</TableCell>
                                        <TableCell>
                                            {pub.doi && (
                                                <IconButton
                                                    href={pub.doi}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <LinkIcon color="action" />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {pub.pdf && (
                                                <IconButton
                                                    href={pub.pdf}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <PictureAsPdfIcon color="action" />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </DialogContent>
                </Dialog>
            </Box>
            {!newPlan && <HistoryLogElement xplans={xplans} label={"publications"} />}
        </div>
    );
};

export default Publications;